import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ReportData, Movimentacao } from '../../../pages/reports/interfaces/report-data';
import moment from 'moment';

export const ExcelService = {
    exportToExcel: async (data: ReportData, reportType: string, projectName?: string): Promise<void> => {
        console.log('ExcelService - data:', data);
        console.log('ExcelService - reportType:', reportType);
        console.log('ExcelService - projectName:', projectName);
        
        try {
            const workbook = new ExcelJS.Workbook();
            
            workbook.creator = 'Sistema Gestore';
            workbook.lastModifiedBy = 'Sistema Gestore';
            workbook.created = new Date();
            workbook.modified = new Date();
            
            const worksheet = workbook.addWorksheet(`Relatório de ${reportType}`);
            const movementHeaders = ['Descrição', 'Beneficiário', 'Rubrica', 'Tipo', 'Data', 'Valor (R$)'];
            const getBeneficiaryLabel = (item: Movimentacao) =>
                item.beneficiaryName ||
                item.nomeBeneficiario ||
                (typeof item.beneficiary === 'string' ? item.beneficiary : item.beneficiary?.name) ||
                '-';
            
            // Usar o nome do projeto dos dados se não for fornecido
            const nomeProject = projectName || data.name || 'Projeto';
            
            // Título principal
            worksheet.mergeCells('A1:F1');
            const titleCell = worksheet.getCell('A1');
            titleCell.value = `Relatório de ${reportType}`;
            titleCell.font = { size: 18, bold: true, color: { argb: '4AAA9C' } };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
            
            // Nome do projeto
            if (nomeProject) {
                worksheet.mergeCells('A2:F2');
                const projectCell = worksheet.getCell('A2');
                projectCell.value = `Projeto: ${nomeProject}`;
                projectCell.font = { size: 14, color: { argb: '666666' } };
                projectCell.alignment = { horizontal: 'center', vertical: 'middle' };
            }
            
            // Data de geração
            worksheet.mergeCells('A3:F3');
            const dateCell = worksheet.getCell('A3');
            dateCell.value = `Gerado em: ${new Date().toLocaleDateString('pt-BR')}`;
            dateCell.font = { size: 10, color: { argb: '999999' } };
            dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
            
            // Período do relatório (se disponível)
            let currentRow = 4;
            if (data.filtros?.periodoInicio && data.filtros?.periodoFim) {
                const periodoInicio = moment(data.filtros.periodoInicio).format('DD/MM/YYYY');
                const periodoFim = moment(data.filtros.periodoFim).format('DD/MM/YYYY');
                
                worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
                const periodoCell = worksheet.getCell(`A${currentRow}`);
                periodoCell.value = `Período: ${periodoInicio} a ${periodoFim}`;
                periodoCell.font = { size: 10, color: { argb: '666666' } };
                periodoCell.alignment = { horizontal: 'center', vertical: 'middle' };
                currentRow++;
            }
            
            currentRow++;

            // Função para formatar data
            const formatDate = (date: string | Date) => {
                return moment(date).format("DD/MM/YYYY");
            };
            
            // Determinar o tipo de dados baseado no tipo do relatório e nos dados
            const getDataType = () => {
                // Se tipoRelatorio existe no resumo, usar essa informação
                if (data.resumo?.tipoRelatorio) {
                    const tipoRelatorio = data.resumo.tipoRelatorio.toLowerCase();
                    if (tipoRelatorio.includes('rendimento') || tipoRelatorio.includes('receita')) {
                        return 'receitas';
                    }
                    if (tipoRelatorio.includes('despesa') || tipoRelatorio.includes('gasto')) {
                        return 'despesas';
                    }
                }
                
                // Verificar movimentações
                if (data?.movimentacoes && data.movimentacoes.length > 0) {
                    const tipos = data.movimentacoes.map(m => m.tipo.toLowerCase());
                    const hasReceitas = tipos.some(t => t.includes('rendimento') || t.includes('receita'));
                    const hasDespesas = tipos.some(t => t.includes('despesa') || t.includes('gasto'));
                    
                    if (hasReceitas && hasDespesas) return 'ambos';
                    if (hasReceitas) return 'receitas';
                    if (hasDespesas) return 'despesas';
                }
                
                // Verificar arrays separados
                const hasReceitas = data?.receitas && data.receitas.length > 0;
                const hasDespesas = data?.despesas && data.despesas.length > 0;
                
                if (hasReceitas && hasDespesas) return 'ambos';
                if (hasReceitas) return 'receitas';
                if (hasDespesas) return 'despesas';
                
                return 'nenhum';
            };
            
            const dataType = getDataType();
            console.log('ExcelService - dataType determined:', dataType);

            // Função para ordenar dados por data (decrescente)
            const sortData = (items: Movimentacao[]) => {
                return [...items].sort((a, b) => {
                    const dateA = new Date(a.data).getTime();
                    const dateB = new Date(b.data).getTime();
                    return dateB - dateA;
                });
            };
            
            // Processar movimentações
            if (data?.movimentacoes && data.movimentacoes.length > 0) {
                // Separar por tipo se necessário
                const receitas = data.movimentacoes.filter(m => 
                    m.tipo.toLowerCase().includes('rendimento') || 
                    m.tipo.toLowerCase().includes('receita')
                );
                const despesas = data.movimentacoes.filter(m => 
                    m.tipo.toLowerCase().includes('despesa') || 
                    m.tipo.toLowerCase().includes('gasto')
                );
                
                if (dataType === 'despesas' || (despesas.length > 0 && receitas.length === 0)) {
                    // Apenas despesas
                    const headerRow = worksheet.getRow(currentRow);
                    headerRow.values = movementHeaders;
                    headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
                    headerRow.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'D32F2F' }
                    };
                    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
                    currentRow++;
                    
                    const sortedDespesas = sortData(despesas.length > 0 ? despesas : data.movimentacoes);
                    sortedDespesas.forEach(item => {
                        const row = worksheet.getRow(currentRow);
                        row.values = [
                            item.descricao || 'Sem descrição',
                            getBeneficiaryLabel(item),
                            item.rubrica || '-',
                            item.tipo || '-',
                            formatDate(item.data),
                            item.valor / 100
                        ];
                        row.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow++;
                    });
                    currentRow += 2;
                }
                else if (dataType === 'receitas' || (receitas.length > 0 && despesas.length === 0)) {
                    // Apenas receitas/rendimentos
                    const headerRow = worksheet.getRow(currentRow);
                    headerRow.values = movementHeaders;
                    headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
                    headerRow.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: '2E7D32' }
                    };
                    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
                    currentRow++;
                    
                    const sortedReceitas = sortData(receitas.length > 0 ? receitas : data.movimentacoes);
                    sortedReceitas.forEach(item => {
                        const row = worksheet.getRow(currentRow);
                        row.values = [
                            item.descricao || 'Sem descrição',
                            getBeneficiaryLabel(item),
                            item.rubrica || '-',
                            item.tipo || '-',
                            formatDate(item.data),
                            item.valor / 100
                        ];
                        row.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow++;
                    });
                    currentRow += 2;
                }
                else if (dataType === 'ambos') {
                    // Seção de Receitas
                    if (receitas.length > 0) {
                        worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
                        const receitasTitle = worksheet.getCell(`A${currentRow}`);
                        receitasTitle.value = 'RECEITAS/RENDIMENTOS';
                        receitasTitle.font = { size: 14, bold: true, color: { argb: '2E7D32' } };
                        receitasTitle.alignment = { horizontal: 'left', vertical: 'middle' };
                        currentRow += 2;
                        
                        const receitasHeaderRow = worksheet.getRow(currentRow);
                        receitasHeaderRow.values = movementHeaders;
                        receitasHeaderRow.font = { bold: true, color: { argb: 'FFFFFF' } };
                        receitasHeaderRow.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: '2E7D32' }
                        };
                        receitasHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
                        currentRow++;
                        
                        let totalReceitas = 0;
                        const sortedReceitas = sortData(receitas);
                        sortedReceitas.forEach(item => {
                            const row = worksheet.getRow(currentRow);
                            row.values = [
                                item.descricao || 'Sem descrição',
                                getBeneficiaryLabel(item),
                                item.rubrica || '-',
                                item.tipo || '-',
                                formatDate(item.data),
                                item.valor / 100
                            ];
                            row.getCell(6).numFmt = 'R$ #,##0.00';
                            totalReceitas += item.valor;
                            currentRow++;
                        });
                        
                        const totalReceitasRow = worksheet.getRow(currentRow);
                        totalReceitasRow.values = ['', '', '', '', 'Total de Receitas:', totalReceitas / 100];
                        totalReceitasRow.font = { bold: true, color: { argb: '2E7D32' } };
                        totalReceitasRow.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow += 2;
                    }
                    
                    // Seção de Despesas
                    if (despesas.length > 0) {
                        worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
                        const despesasTitle = worksheet.getCell(`A${currentRow}`);
                        despesasTitle.value = 'DESPESAS';
                        despesasTitle.font = { size: 14, bold: true, color: { argb: 'D32F2F' } };
                        despesasTitle.alignment = { horizontal: 'left', vertical: 'middle' };
                        currentRow += 2;
                        
                        const despesasHeaderRow = worksheet.getRow(currentRow);
                        despesasHeaderRow.values = movementHeaders;
                        despesasHeaderRow.font = { bold: true, color: { argb: 'FFFFFF' } };
                        despesasHeaderRow.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'D32F2F' }
                        };
                        despesasHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
                        currentRow++;
                        
                        let totalDespesas = 0;
                        const sortedDespesas = sortData(despesas);
                        sortedDespesas.forEach(item => {
                            const row = worksheet.getRow(currentRow);
                            row.values = [
                                item.descricao || 'Sem descrição',
                                getBeneficiaryLabel(item),
                                item.rubrica || '-',
                                item.tipo || '-',
                                formatDate(item.data),
                                item.valor / 100
                            ];
                            row.getCell(6).numFmt = 'R$ #,##0.00';
                            totalDespesas += item.valor;
                            currentRow++;
                        });
                        
                        const totalDespesasRow = worksheet.getRow(currentRow);
                        totalDespesasRow.values = ['', '', '', '', 'Total de Despesas:', totalDespesas / 100];
                        totalDespesasRow.font = { bold: true, color: { argb: 'D32F2F' } };
                        totalDespesasRow.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow += 2;
                    }
                }
            }
            // Processar arrays separados (compatibilidade)
            else if (data?.receitas || data?.despesas) {
                if (data.receitas && data.receitas.length > 0) {
                    const headerRow = worksheet.getRow(currentRow);
                    headerRow.values = movementHeaders;
                    headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
                    headerRow.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: '2E7D32' }
                    };
                    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
                    currentRow++;
                    
                    data.receitas.forEach(item => {
                        const row = worksheet.getRow(currentRow);
                        row.values = [
                            item.descricao || 'Sem descrição',
                            getBeneficiaryLabel(item),
                            item.rubrica || '-',
                            item.tipo || '-',
                            formatDate(item.data),
                            item.valor / 100
                        ];
                        row.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow++;
                    });
                    currentRow += 2;
                }
                
                if (data.despesas && data.despesas.length > 0) {
                    const headerRow = worksheet.getRow(currentRow);
                    headerRow.values = movementHeaders;
                    headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
                    headerRow.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'D32F2F' }
                    };
                    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
                    currentRow++;
                    
                    data.despesas.forEach(item => {
                        const row = worksheet.getRow(currentRow);
                        row.values = [
                            item.descricao || 'Sem descrição',
                            getBeneficiaryLabel(item),
                            item.rubrica || '-',
                            item.tipo || '-',
                            formatDate(item.data),
                            item.valor / 100
                        ];
                        row.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow++;
                    });
                    currentRow += 2;
                }
            }
            
            // Resumo Financeiro
            if (data?.resumo) {
                currentRow++;
                worksheet.mergeCells(`A${currentRow}:F${currentRow}`);
                const resumoTitle = worksheet.getCell(`A${currentRow}`);
                resumoTitle.value = 'RESUMO FINANCEIRO';
                resumoTitle.font = { size: 14, bold: true, color: { argb: '4AAA9C' } };
                resumoTitle.alignment = { horizontal: 'center', vertical: 'middle' };
                resumoTitle.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'F5F5F5' }
                };
                currentRow += 2;
                
                // Valor do Projeto (se disponível)
                if (data.resumo.valorProjeto) {
                    const valorProjetoRow = worksheet.getRow(currentRow);
                    valorProjetoRow.values = ['Valor do Projeto', '', '', '', '', data.resumo.valorProjeto / 100];
                    valorProjetoRow.font = { bold: true };
                    valorProjetoRow.getCell(6).numFmt = 'R$ #,##0.00';
                    currentRow++;
                }
                
                // Mostrar totais baseado no tipo de dados
                if (dataType === 'despesas') {
                    if (data.resumo.totalDespesas !== undefined) {
                        const despesasResumoRow = worksheet.getRow(currentRow);
                        despesasResumoRow.values = ['Total de Despesas', '', '', '', '', data.resumo.totalDespesas / 100];
                        despesasResumoRow.getCell(1).font = { bold: true };
                        despesasResumoRow.getCell(6).font = { color: { argb: 'D32F2F' } };
                        despesasResumoRow.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow++;
                    }
                    
                    if (data.resumo.saldo !== undefined) {
                        const saldoRow = worksheet.getRow(currentRow);
                        saldoRow.values = ['Saldo Disponível', '', '', '', '', data.resumo.saldo / 100];
                        saldoRow.font = { bold: true };
                        const saldoCell = saldoRow.getCell(6);
                        saldoCell.numFmt = 'R$ #,##0.00';
                        saldoCell.font = { 
                            color: { argb: data.resumo.saldo >= 0 ? '2E7D32' : 'D32F2F' },
                            bold: true,
                            size: 12
                        };
                        currentRow++;
                    }
                }
                else if (dataType === 'receitas') {
                    if (data.resumo.totalReceitas !== undefined) {
                        const receitasResumoRow = worksheet.getRow(currentRow);
                        receitasResumoRow.values = ['Total de Receitas', '', '', '', '', data.resumo.totalReceitas / 100];
                        receitasResumoRow.getCell(1).font = { bold: true };
                        receitasResumoRow.getCell(6).font = { color: { argb: '2E7D32' } };
                        receitasResumoRow.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow++;
                    }
                    
                    if (data.resumo.saldo !== undefined) {
                        const saldoRow = worksheet.getRow(currentRow);
                        saldoRow.values = ['Total Arrecadado', '', '', '', '', data.resumo.saldo / 100];
                        saldoRow.font = { bold: true };
                        const saldoCell = saldoRow.getCell(6);
                        saldoCell.numFmt = 'R$ #,##0.00';
                        saldoCell.font = { 
                            color: { argb: '2E7D32' },
                            bold: true,
                            size: 12
                        };
                        currentRow++;
                    }
                }
                else if (dataType === 'ambos') {
                    if (data.resumo.totalReceitas !== undefined) {
                        const receitasResumoRow = worksheet.getRow(currentRow);
                        receitasResumoRow.values = ['Total de Receitas', '', '', '', '', data.resumo.totalReceitas / 100];
                        receitasResumoRow.getCell(1).font = { bold: true };
                        receitasResumoRow.getCell(6).font = { color: { argb: '2E7D32' } };
                        receitasResumoRow.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow++;
                    }
                    
                    if (data.resumo.totalDespesas !== undefined) {
                        const despesasResumoRow = worksheet.getRow(currentRow);
                        despesasResumoRow.values = ['Total de Despesas', '', '', '', '', data.resumo.totalDespesas / 100];
                        despesasResumoRow.getCell(1).font = { bold: true };
                        despesasResumoRow.getCell(6).font = { color: { argb: 'D32F2F' } };
                        despesasResumoRow.getCell(6).numFmt = 'R$ #,##0.00';
                        currentRow++;
                    }
                    
                    if (data.resumo.saldo !== undefined) {
                        const saldoRow = worksheet.getRow(currentRow);
                        saldoRow.values = ['Saldo Líquido', '', '', '', '', data.resumo.saldo / 100];
                        saldoRow.font = { bold: true };
                        const saldoCell = saldoRow.getCell(6);
                        saldoCell.numFmt = 'R$ #,##0.00';
                        saldoCell.font = { 
                            color: { argb: data.resumo.saldo >= 0 ? '2E7D32' : 'D32F2F' },
                            bold: true,
                            size: 12
                        };
                        currentRow++;
                    }
                }
                
                // Percentual de execução (se disponível)
                if (data.resumo.percentualExecucao !== undefined) {
                    const percentualRow = worksheet.getRow(currentRow);
                    percentualRow.values = ['Percentual de Execução', '', '', '', '', `${data.resumo.percentualExecucao.toFixed(2)}%`];
                    percentualRow.font = { bold: true };
                    percentualRow.getCell(6).font = { color: { argb: '4AAA9C' } };
                    currentRow++;
                }
            }
            
            // Configurar largura das colunas
            worksheet.columns = [
                { width: 35 }, // Descrição
                { width: 28 }, // Beneficiário
                { width: 25 }, // Rubrica
                { width: 20 }, // Tipo
                { width: 15 }, // Data
                { width: 20 }  // Valor
            ];
            
            // Adicionar bordas a todas as células com dados
            for (let i = 1; i <= currentRow; i++) {
                const row = worksheet.getRow(i);
                row.eachCell({ includeEmpty: false }, (cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            }
            
            // Gerar o arquivo
            const buffer = await workbook.xlsx.writeBuffer();
            
            const blob = new Blob([buffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            const fileName = `${nomeProject}_${reportType}_${new Date().toISOString().slice(0, 10)}.xlsx`;
            
            saveAs(blob, fileName);
            
        } catch (error) {
            console.error('Erro ao gerar relatório Excel:', error);
            throw new Error('Erro ao gerar relatório Excel: ' + error);
        }
    }
};

export default ExcelService;
